import { useMemo, useState, type FC, useRef } from 'react';
import type { HeroDetailedProps } from './types';

import styled, { css } from 'styled-components';
import {
  COLOR_YELLOW,
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText,
  shade,
  generateAccessKey
} from 'shared/helpers/index';
import { useBook, useSales, useWebsiteEditor, useSublineColor } from 'shared/hooks/index';

import { Button, Wrapper, Mockup, Download } from '../Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import { Image as ImageComp } from 'shared/components/Common';
import { ConditionalImage } from '../ConditionalImage';
import useGeneralActions from '../hooks/useGeneralActions';

const HeroDetailed = styled.div`
  position: relative;
  min-height: 693px;
  padding-bottom: 30px;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
  flex-direction: column;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
  overflow: hidden;
  `}
`;

const StyledWrapper = styled((props) => <Wrapper {...props} />)`
  display: flex;
  justify-content: space-between;
  padding: 70px 0 10px 0px;

  ${({ theme: { $width } }) =>
    $width <= 1100 &&
    `
    flex-direction: column;
      padding: 70px 0 48px 0px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: column-reverse;
    `}

      ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
      padding: 50px 0 48px 0px;
    `}
`;

const TitleWrapper = styled.div`
  width: 655px;
  position: relative;
  z-index: 7;
  height: fit-content;
  margin-bottom: 235px;
  padding-top: 30px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
      width: 502px;
      margin-bottom: 205px;
      padding-top: 16px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 1100 &&
    `
      padding-top: 0px;
    width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 60px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin-top: 40px;
    margin-bottom: 0px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    padding: 0 15px;
    `}
`;

const Title = styled.h1<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $largeHeadlineFont } }) => $largeHeadlineFont};
  line-height: 1.15em;
  color: #fff;
  letter-spacing: -0.01em;
  margin-bottom: 25px;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    margin-bottom: 14px;
    `}
`;

const Description = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  margin-bottom: 38px;
  line-height: 1.35em;
  color: #fff;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    margin-bottom: 29px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin-bottom: 40px;
    `}
`;

const BtnInner = styled.div`
  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
  `}
`;

const buttonStyles = css`
  max-width: 415px;
  display: inline-block;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
      max-width: 345px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
      max-width: 380px;
      padding-top: 24px;
      padding-bottom: 26px;
    `}
`;

const DownloadStyled = styled(Download)`
  ${buttonStyles}
`;

const StyledButton = styled(Button)`
  ${buttonStyles}
`;

const RelativeWrapper = styled.div`
  position: relative;
  z-index: 12;
  width: 42%;
  display: flex;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: 792px) {
    width: 68%;
    margin: 0 auto;
  }

  @media screen and (max-width: 575px) {
    width: 92%;
  }
`;

const StyledMockup = styled((props) => <Mockup {...props} />)`
  width: 542px;
  height: auto;
  object-fit: cover;
  object-position: center center;
  z-index: 2;
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 438px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 340px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 370 &&
    `
    width: 270px;
  `}
`;

const StyledWrapperBenefits = styled((props) => <Wrapper {...props} />)`
  padding: 0px 0px 10px;
  margin-top: -170px;
  position: relative;
  z-index: 5;

  ${({ theme: { $width } }) =>
    $width <= 1100 &&
    `
    margin-top: 0px;
    `}
`;

const BenefitWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 715px;
  z-index: 12;
  bottom: 20px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 502px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 1100 &&
    `
    width: 100%;
    `}

    ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    padding: 0 15px 80px;
      flex-direction: column;
      gap: 15px;
      `}
`;

const BenefitItem = styled.div`
  width: 31.5%;
  padding: 17px 13px 28px;
  border-radius: 17px;
  background: #fff;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 32%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 1100 &&
    `
    flex-direction: row;
    align-items: flex-start;
    text-align: left;
    padding: 17px 13px 20px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    padding: 18px 18px 20px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    padding: 18px 15px;
    border-radius: 12px;
    `}
`;

const Svg = styled.svg`
  width: 62px;
  height: 62px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
      width: 52px;
      height: 52px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 1100 &&
    `
    width: 42px;
    height: 42px;
    margin-bottom: 8px;
  `}

    ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 52px;
    height: 52px;
    margin-bottom: 8px;
  `}
`;

const BenefitTxtInner = styled.div`
  width: 100%;

  ${({ theme: { $width } }) =>
    $width <= 1100 &&
    `
      width: 76%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
      width: 80%;
    `}
`;

const TitleBenefit = styled.h3<{ isEmpty?: boolean; editable?: boolean }>`
  margin-bottom: 4px;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  font-weight: 500;
  line-height: 1.35em;

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    margin-bottom: 4px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
      display: ${editable ? 'block' : 'none'};
      &:not(:focus):before {
        content: attr(data-placeholder);
        cursor: text;
        color: #aaa;
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    `
      : `
      &:before {
        content: none;
      } 
    `}
`;

const TextBenefit = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  line-height: 1.3em;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const WaveBg = styled.div`
  width: 100%;
  height: 320px;
  position: absolute;
  bottom: -1px;
  z-index: 2;
  display: flex;
  justify-content: center;
  overflow: hidden;

  ${({ theme: { $width } }) =>
    $width <= 1100 &&
    `
  height: 100px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    bottom: -2px;
  `}
`;

const RelativeInnerSvg = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const WaveSvg = styled.svg`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  height: 100%;
  pointer-events: none;
  z-index: 1;
`;

const ColoredBg = styled.div`
  position: absolute;
  width: 100%;
  height: 99.5%;
  top: 0;

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    height: 100%;
    `}
`;

const ImgInner = styled.div`
  width: 100%;
  position: relative;
  z-index: 36;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const imageStyles = css`
  position: relative;
  overflow: hidden;
`;

const HoverableDropzoneStyled = styled(ConditionalImage)<{ width: number; height: number }>`
  max-width: 100%;
  max-height: 980px;
  position: relative;
  overflow: hidden;
  width: 100%;
  height: auto !important;
  aspect-ratio: ${({ width, height }) => (width && height ? `${width} / ${height}` : '513 / 685')};
  object-fit: cover;
  display: flex;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: 991px) {
    width: 100% !important;
    height: auto !important;
    aspect-ratio: ${({ width, height }) =>
      width && height ? `${width} / ${height}` : '513 / 685'};
    object-fit: cover;
  }
`;

const BookMediaImg = styled(ImageComp)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

export const V4: FC<HeroDetailedProps> = (props) => {
  const {
    mockup,
    cover_thumbnail,
    title = defaultWebValue,
    content = defaultWebValue,
    button = defaultWebValue,
    backgroundColor = COLOR_YELLOW,
    textColor,
    benefits = [],
    customBackgroundColor,
    meta,
    hidden,
    order
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();
  const { isCoverDisabled, pages, bookMedia } = useBook();
  const { pagesThumbnails, purchaseElementId, updateImageMeta, type, renderSales, isDashboard } =
    useSales();
  const { handleUploadAction } = useGeneralActions(props);
  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });
  const [sublineColor, setSublineColor] = useState<string>('#fff');
  const topRef = useRef<HTMLDivElement>(null);

  useSublineColor(topRef, setSublineColor, renderSales);

  const [isLoading, setIsLoading] = useState(false);

  const { checkBenefits: checkTitleBenefits } = useIsEmpty(meta, 'title', true);
  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);

  const coverThumbnail = useMemo(() => {
    if (isCoverDisabled && (pages.length > 0 || pagesThumbnails.length > 0)) {
      return pagesThumbnails[0] || pages[0]?.image_preview;
    }
    return cover_thumbnail;
  }, [isCoverDisabled, pages, pagesThumbnails, cover_thumbnail]);

  const bookMockup = useMemo(() => {
    if (meta['image'] && !meta['image_placeholder']) {
      return meta['image'];
    }

    const media = bookMedia?.find((media) => media.name === 'book_mockup');
    return media?.url;
  }, [bookMedia, meta]);

  const resolvedBackgroundColor = useMemo(() => {
    return customBackgroundColor ? customBackgroundColor : shade(backgroundColor, 0.7);
  }, [backgroundColor, customBackgroundColor]);

  const hasBenefits = useMemo(() => benefits.length > 0, [benefits]);

  const buttonColor = useMemo(() => button?.color, [button]);

  const waveFillColor = useMemo(() => sublineColor || '#fff', [sublineColor]);

  // Accessibility improvements
  const titleAccessKey = useMemo(() => generateAccessKey('ht', order), [order]);
  const contentAccessKey = useMemo(() => generateAccessKey('hc', order), [order]);
  const buttonAccessKey = useMemo(() => generateAccessKey('hb', order), [order]);
  const skipLinkId = useMemo(() => `skip-to-hero-${meta['accessorKey'] || 'hero'}`, [meta]);

  const buttonType = useMemo(() => {
    return meta['url']
      ? 'external link'
      : type === 'delivery'
        ? 'delivery action'
        : 'purchase button';
  }, [meta, type]);

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Hero edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleModalError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Button action error: ${error instanceof Error ? error.message : 'Action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUploadError = (error: Error | unknown) => {
    // Announce upload error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Image upload error: ${error instanceof Error ? error.message : 'Upload failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUpload = async (file: File) => {
    try {
      setIsLoading(true);
      await handleUploadAction(file, setResize, 'book_mockup');
      setIsLoading(false);
    } catch (error) {
      handleUploadError(error);
      setIsLoading(false);
    }
  };

  return (
    <HeroDetailed
      ref={topRef}
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1
      }}
      aria-label="Hero section with wave design and overlapping benefits"
      role="region">
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to hero detailed content</a>
      </span>

      <StyledWrapper id={skipLinkId}>
        <TitleWrapper id="HeadlineSection">
          <Title
            id={title.id}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(title.val, isDashboard)
            }}
            style={{ color: filterBackgroundColor(resolvedBackgroundColor, textColor) }}
            isEmpty={isTitleEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'title',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  'aria-label':
                    isTitleEmpty || !title.val?.trim() ? 'Hero section main title' : undefined,
                  accessKey: titleAccessKey,
                  title: `Press Alt+${titleAccessKey} to focus this hero title`,
                  tabIndex: 0
                })}
          />

          <Description
            id={content.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
            style={{ color: filterBackgroundColor(resolvedBackgroundColor, textColor) }}
            isEmpty={isContentEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'content',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  role: 'textbox',
                  'aria-multiline': 'true',
                  'aria-label':
                    isContentEmpty || !content.val?.trim() ? 'Hero section description' : undefined,
                  'aria-describedby': title.id,
                  accessKey: contentAccessKey,
                  title: `Press Alt+${contentAccessKey} to focus this hero description`,
                  tabIndex: 0
                })}
          />

          <BtnInner role="group" aria-label={`Hero action area - ${buttonType}`}>
            {meta['url'] || type === 'delivery' ? (
              <DownloadStyled
                textColor={textColor}
                button={button}
                meta={meta}
                outerOnClick={() => {
                  if (editable) {
                    try {
                      // empty needs only for error
                    } catch (error) {
                      handleModalError(error);
                    }
                  }
                }}
                {...(!isDashboard && {
                  accessKey: buttonAccessKey,
                  title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                  'aria-describedby': `${title.id} ${content.id}`
                })}
              />
            ) : (
              <StyledButton
                to={purchaseElementId}
                button={button}
                meta={meta}
                outerOnClick={() => {
                  if (editable) {
                    try {
                      // empty needs only for error
                    } catch (error) {
                      handleModalError(error);
                    }
                  }
                }}
                {...(!isDashboard && {
                  accessKey: buttonAccessKey,
                  title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                  'aria-describedby': `${title.id} ${content.id}`
                })}
              />
            )}
          </BtnInner>
        </TitleWrapper>

        <RelativeWrapper>
          <ImgInner role="img" aria-label="Book preview and mockup">
            {bookMockup ? (
              <HoverableDropzoneStyled
                width={resize.width}
                height={resize.height}
                isSalePage
                css={imageStyles as never}
                disabled={!editable}
                enableLinks
                updateMeta={(value) => updateImageMeta(meta['accessorKey'], value, 'image_link')}
                onUpload={handleUpload}
                loading={isLoading}
                alreadyUploaded={Boolean(bookMockup)}
                imageFile={bookMockup}
                resizeSelector={editable}
                handleResize={({ width, height }) => {
                  setResize({ width, height });
                  updateImageMeta(meta['accessorKey'], width, 'width');
                  updateImageMeta(meta['accessorKey'], height, 'height');
                }}
                initialPosition={{
                  x: meta['x_1'] || 0,
                  y: meta['y_1'] || 0,
                  width: meta['d_width_1'],
                  height: meta['d_height_1']
                }}
                meta={meta}
                positionEnabled
                handlePosition={(width: number, height: number, x: number, y: number) => {
                  updateImageMeta(meta['accessorKey'], x, 'x_1');
                  updateImageMeta(meta['accessorKey'], y, 'y_1');
                  updateImageMeta(meta['accessorKey'], width, 'd_width_1');
                  updateImageMeta(meta['accessorKey'], height, 'd_height_1');
                }}
                imageAspectRatio={{
                  ratio: '3x4',
                  pixels:
                    resize.width && resize.height ? `${resize.width}x${resize.height}` : '600x800'
                }}
                ariaLabel="Book mockup image - drag and drop to upload new image">
                <BookMediaImg
                  src={bookMockup}
                  alt="Book mockup preview showing cover design"
                  width={resize.width || 500}
                  height={resize.height || 500}
                  priority
                />
              </HoverableDropzoneStyled>
            ) : (
              <StyledMockup
                mockup={mockup?.file}
                mockup_id={mockup?.id}
                cover_thumbnail={coverThumbnail}
                aria-label="Book mockup with cover thumbnail"
              />
            )}
          </ImgInner>
        </RelativeWrapper>
      </StyledWrapper>

      {hasBenefits && (
        <StyledWrapperBenefits style={{ padding: '0px 0px 10px' }}>
          <BenefitWrapper
            role="region"
            aria-label="Key benefits and features overlapping the design">
            {benefits.map(({ content, icon, title }, index) => {
              const isTitleBenEmpty = checkTitleBenefits(index);
              const isContentBenEmpty = checkContentBenefits(index);
              const benefitAccessKey = generateAccessKey('hben', index);

              return (
                <BenefitItem
                  key={`${icon?.toString()}-${index}`}
                  role="article"
                  aria-label={`Overlapping benefit ${index + 1}: ${title?.val || 'Key feature'}`}
                  {...(!isDashboard && {
                    accessKey: benefitAccessKey,
                    title: `Press Alt+${benefitAccessKey} to focus overlapping benefit ${index + 1}`,
                    tabIndex: 0
                  })}>
                  <Svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 62 62"
                    aria-hidden="true"
                    role="presentation"
                    focusable="false">
                    <circle cx="31" cy="31" r="31" strokeWidth={0} fill={buttonColor} />
                    <path
                      d="m44.85,23.17c-.13.64-.53,1.1-.97,1.55-5.07,5.21-10.13,10.43-15.19,15.65-.4.41-.85.64-1.41.46-.23-.07-.46-.21-.63-.38-2.52-2.58-5.03-5.17-7.54-7.76-.86-.89-.84-2.14.01-3.03.28-.3.57-.59.86-.88.87-.88,2.08-.88,2.94,0,1.5,1.54,3,3.09,4.5,4.63.07.07.1.16.2.31.12-.17.17-.26.25-.34,4.15-4.28,8.3-8.56,12.46-12.84.98-1.01,2.15-1.01,3.13,0,.14.14.27.29.42.43.46.43.86.91.99,1.57v.64h0Z"
                      fill="#fff"
                      strokeWidth={0}
                    />
                  </Svg>
                  <BenefitTxtInner>
                    <TitleBenefit
                      id={title?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(title?.val, editable) }}
                      style={{ color: filterBackgroundColor('#fff', textColor) }}
                      isEmpty={isTitleBenEmpty}
                      {...(isDashboard
                        ? {
                            onClick: (e: React.MouseEvent<HTMLElement>) => {
                              try {
                                updateActiveElement(e);
                              } catch (error) {
                                handleEditError(error);
                              }
                            },
                            onBlur: resetActiveElement,
                            'data-accessor-parent': meta['accessorKey'],
                            'data-accessorkey': 'title',
                            'data-benefits': true,
                            'data-placeholder': '+',
                            editable: true
                          }
                        : {
                            'aria-label': !title?.val?.trim()
                              ? `Overlapping benefit ${index + 1} title`
                              : undefined
                          })}
                    />
                    <TextBenefit
                      id={content?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(content?.val, editable) }}
                      style={{ color: filterBackgroundColor('#fff', textColor) }}
                      isEmpty={isContentBenEmpty}
                      {...(isDashboard
                        ? {
                            onClick: (e: React.MouseEvent<HTMLElement>) => {
                              try {
                                updateActiveElement(e);
                              } catch (error) {
                                handleEditError(error);
                              }
                            },
                            onBlur: resetActiveElement,
                            'data-accessor-parent': meta['accessorKey'],
                            'data-accessorkey': 'content',
                            'data-benefits': true,
                            'data-placeholder': '+',
                            editable: true
                          }
                        : {
                            'aria-label': !content?.val?.trim()
                              ? `Overlapping benefit ${index + 1} description`
                              : undefined,
                            'aria-describedby': title?.id
                          })}
                    />
                  </BenefitTxtInner>
                </BenefitItem>
              );
            })}
          </BenefitWrapper>
        </StyledWrapperBenefits>
      )}

      <WaveBg aria-hidden="true" role="presentation">
        <RelativeInnerSvg>
          <WaveSvg
            style={{ fill: waveFillColor }}
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            viewBox="0 0 3346.03 276.63"
            aria-hidden="true"
            role="presentation"
            focusable="false">
            <path d="m0,82.85S139.14-28.26,393.98,8.23c245.13,35.1,558.65,220.91,868.91,198.4,166.62-12.09,293.59-51.61,410.12-110.31,118.76-59.82,284.47-109.03,466.81-93.39,267.86,22.97,351.99,131.1,555.53,149.63,325.64,29.64,650.67-69.71,650.67-69.71v193.78H0V82.85Z" />
          </WaveSvg>
        </RelativeInnerSvg>
      </WaveBg>
      <ColoredBg
        style={{ backgroundColor: resolvedBackgroundColor }}
        aria-hidden="true"
        role="presentation"
      />
    </HeroDetailed>
  );
};

V4.displayName = 'HeroDetailedV4';
